// src/services/api.js
// Connector for cPanel PHP API functions
import { getAdminSettingsDB, getAllUsers, getAllBookings } from './db.js';
import { t } from '../i18n.js';

// Get the base API URL (relative if deployed, localhost or full domain if testing)
// In Vite Dev, `/api` is securely proxied to `http://127.0.0.1/onng-lineoa/api` via vite.config.js
// In Production, `/api` naturally points to the cPanel nested folder
const API_BASE = '/api';

function renderTemplate(tpl, booking, settings = {}) {
    if (!tpl) return '';
    return tpl
        .replace(/{{customer_name}}/g, booking.customer_name || 'ลูกค้า')
        .replace(/{{date_time}}/g, `${booking.date || ''} เวลา ${booking.time || ''} น.`)
        .replace(/{{service_name}}/g, booking.service_type || 'บริการดูดวง')
        .replace(/{{price}}/g, booking.price ? booking.price.toLocaleString() : '0')
        .replace(/{{bank_name}}/g, settings.bank_name || '-')
        .replace(/{{account_name}}/g, settings.account_name || '-')
        .replace(/{{account_number}}/g, settings.account_number || '-')
        .replace(/{{promptpay}}/g, settings.promptpay_id || '-')
        .replace(/{{inter_bank_name}}/g, settings.inter_bank_name || 'Krung Thai Bank')
        .replace(/{{inter_swift}}/g, settings.inter_swift || 'KRTHTHBK')
        .replace(/{{inter_branch}}/g, settings.inter_branch || '521')
        .replace(/{{inter_account_name}}/g, settings.inter_account_name || 'Ms. Sassihorn Pattaragrida')
        .replace(/{{inter_account_number}}/g, settings.inter_account_number || '521-050-9974')
        // Ensure accurate domain where frontend lives:
        .replace(/{{form_link}}/g, `${window.location.origin}/#/booking/${booking.id}/payment`);
}

/**
 * Send booking notification immediately via cPanel API
 * @param {string|string[]} userIds 
 * @param {string} messageText 
 */
export async function sendNotification(userIds, messageText, imageUrl = null, flexContent = null) {
    if (!userIds || !messageText) return;
    try {
        const payload = {
            user_id: userIds,
            message: messageText
        };
        if (imageUrl) {
            payload.image_url = imageUrl;
        }
        if (flexContent) {
            payload.flex_content = flexContent;
        }

        const response = await fetch(`${API_BASE}/notify.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        
        const resultText = await response.text();
        console.log('--- LINE API Response ---', resultText);
        
        // Check if PHP spit out any warning text before JSON
        if (resultText.includes('<b>Warning</b>') || resultText.includes('Fatal error')) {
            prompt(`⚠️ พบปัญหาฝั่ง Server (PHP Error):\n(ก๊อปปี้ข้อความด้านล่างนี้ส่งให้ผมดูเลยครับ)`, resultText.substring(0, 500));
            return;
        }

        let result = {};
        try { 
            result = JSON.parse(resultText); 
        } catch(e) {
            console.error('JSON Parse Error:', e);
            prompt(`⚠️ ข้อความจากเซิร์ฟเวอร์อ่านไม่ได้ (อาจจะแฝงบั๊ก):\n(ก๊อปปี้ข้อความด้านล่างส่งให้ผมดูครับ)`, resultText.substring(0, 200));
            return;
        }
        
        if (result.status === 'error') {
            console.error('❌ Failed to push LINE message:', result);
            // prompt(`ไม่สามารถส่งข้อความ LINE ได้\n(ก๊อปปี้ข้อความด้านล่างส่งให้ผมดูครับ):`, JSON.stringify(result.details || result.message));
        } else if (!response.ok) {
            console.error('❌ Failed (Network/Server):', resultText);
            // prompt(`❌ ระบบขัดข้อง (HTTP ${response.status})\n(ก๊อปปี้ข้อความด้านล่างส่งให้ผมดูครับ):`, resultText.substring(0, 200));
        } else {
            console.log('✅ LINE message sent successfully!');
            // Optional: alert('ส่งข้อความสำเร็จ!');
        }
    } catch (e) {
        console.error('Network Error calling cPanel API:', e);
        prompt(`การเชื่อมต่อไปยังตัวส่งข้อความขัดข้อง\n(โปรดเช็คว่าเปิด Apache ใน XAMPP และเข้าผ่าน localhost แล้ว)`, e.message);
    }
}

/**
 * Notify roles based on Booking event
 */
export async function notifyNewBooking(booking) {
    try {
        const settings = await getAdminSettingsDB() || {};
        
        // 1. Send to Customer
        const defaultCustomer = `🔮 ส่งคำขอจองสำเร็จ!\n\nรูปแบบ: {{service_name}}\nวันที่: {{date_time}}\n\n⏳ กรุณารอแอดมินยืนยันคิวการจอง ก่อนโอนเงิน\n\n📌 สำคัญ: เพื่อเปิดช่องทางติดต่อกับแอดมิน กรุณาพิมพ์คำว่า "ตกลง" หรือสติ๊กเกอร์ 1 ตัว ส่งมาในแชทนี้ด้วยนะคะ`;
        let tpl = settings.template_new_booking || defaultCustomer;
        
        // Remove old wording if it was patched
        if (tpl && tpl.includes('ชำระในประเทศ')) {
            tpl = defaultCustomer;
        }

        const customerMsg = renderTemplate(tpl, booking, settings);
        
        const SERVICE_MAP = {
            onsite: t('service_onsite').replace('🏠 ', ''),
            chat: t('service_chat').replace('💬 ', ''),
            call: t('service_call').replace('📞 ', ''),
            video: t('service_video').replace('📹 ', '')
        };
        const serviceName = SERVICE_MAP[booking.service_type] || booking.service_type || 'บริการดูดวง';
        const dateTimeStr = `${booking.date || ''} เวลา ${booking.time || ''} น.`;

        const flexContent = {
          "type": "bubble",
          "size": "mega",
          "body": {
            "type": "box",
            "layout": "vertical",
            "paddingAll": "xl",
            "contents": [
              { "type": "text", "text": "ASTROLOGY BOOKING", "color": "#D4AF37", "size": "xs", "weight": "bold" },
              { "type": "text", "text": "ส่งคำขอจองสำเร็จ", "weight": "bold", "size": "xxl", "color": "#111111", "margin": "md", "wrap": true },
              { "type": "text", "text": "กรุณารอแอดมินยืนยันคิวการจอง ก่อนโอนเงิน", "size": "sm", "color": "#E59866", "margin": "xs", "wrap": true },
              { "type": "separator", "margin": "xxl" },
              {
                "type": "box",
                "layout": "vertical",
                "margin": "xxl",
                "spacing": "sm",
                "contents": [
                  {
                    "type": "box",
                    "layout": "horizontal",
                    "contents": [
                      { "type": "text", "text": "รูปแบบ", "size": "sm", "color": "#888888" },
                      { "type": "text", "text": serviceName, "size": "sm", "color": "#111111", "align": "end", "wrap": true }
                    ]
                  },
                  {
                    "type": "box",
                    "layout": "horizontal",
                    "contents": [
                      { "type": "text", "text": "วันที่", "size": "sm", "color": "#888888" },
                      { "type": "text", "text": dateTimeStr, "size": "sm", "color": "#111111", "align": "end", "wrap": true }
                    ]
                  },
                  {
                    "type": "box",
                    "layout": "horizontal",
                    "contents": [
                      { "type": "text", "text": "ราคา", "size": "sm", "color": "#888888" },
                      { "type": "text", "text": "฿" + (booking.price || 0).toLocaleString(), "size": "sm", "color": "#D4AF37", "align": "end", "weight": "bold", "wrap": true }
                    ]
                  },
                  { "type": "separator", "margin": "md" },
                  {
                    "type": "box",
                    "layout": "vertical",
                    "margin": "md",
                    "spacing": "sm",
                    "contents": [
                      { "type": "text", "text": "ข้อมูลส่วนตัว (ดวงชะตา)", "size": "xs", "color": "#888888", "weight": "bold" },
                      { "type": "text", "text": "โทร: " + (booking.customer_details?.phone || "-"), "size": "xs", "color": "#555555", "wrap": true },
                      { "type": "text", "text": "ว/ด/ป: " + (booking.customer_details?.dob || "-"), "size": "xs", "color": "#555555", "wrap": true },
                      { "type": "text", "text": "เวลาเกิด: " + (booking.customer_details?.tob || "ไม่ระบุ"), "size": "xs", "color": "#555555", "wrap": true },
                      { "type": "text", "text": "ส่วนสูง/เพศ: " + (booking.customer_details?.height || "-") + " ซม. / " + (booking.customer_details?.gender || "-"), "size": "xs", "color": "#555555", "wrap": true },
                      { "type": "text", "text": "จังหวัด/ประเทศเกิด: " + (booking.customer_details?.pob || "-"), "size": "xs", "color": "#555555", "wrap": true },
                      { "type": "text", "text": "คำถาม: " + (booking.customer_details?.question || "-"), "size": "xs", "color": "#555555", "wrap": true }
                    ]
                  }
                ]
              }
            ]
          },
          "footer": {
            "type": "box", "layout": "vertical", "paddingAll": "lg",
            "backgroundColor": "#fff4f4",
            "contents": [
              {
                "type": "text",
                "text": "📌 สำคัญมาก: เพื่อให้แอดมินติดต่อและตอบคำถามคุณได้ รบกวนพิมพ์คำว่า 'ตกลง' หรือส่งสติ๊กเกอร์ 1 ตัว ตอบกลับในแชทนี้ด้วยนะคะ",
                "wrap": true,
                "size": "sm",
                "color": "#dc3545",
                "weight": "bold",
                "align": "center"
              }
            ]
          }
        };

        console.log('Sending Customer Alert to:', booking.user_id);
        await sendNotification(booking.user_id, "🔮 ส่งคำขอจองคิวเรียบร้อย!", null, flexContent);

        // 2. Send to Admin(s)
        const allBookings = await getAllBookings();
        const bookingsOnDate = allBookings.filter(b => b.date === booking.date).sort((a,b) => (a.created_at || 0) - (b.created_at || 0));
        const myIndex = bookingsOnDate.findIndex(b => b.id === booking.id);
        const queueNumber = myIndex !== -1 ? myIndex + 1 : bookingsOnDate.length + 1;

        const sType = booking.service_type || 'บริการดูดวง';

        const adminFlexNew = {
          "type": "bubble",
          "size": "mega",
          "header": {
            "type": "box",
            "layout": "vertical",
            "paddingAll": "20px",
            "backgroundColor": "#1E3A8A",
            "contents": [
              { "type": "text", "text": "🔔 คิวใหม่เข้า! (New Booking)", "color": "#FFFFFF", "weight": "bold", "size": "lg" }
            ]
          },
          "body": {
            "type": "box", "layout": "vertical", "paddingAll": "xl",
            "contents": [
              { "type": "text", "text": `ลำดับคิวที่ ${queueNumber}`, "weight": "bold", "size": "xxl", "color": "#1E3A8A", "margin": "none" },
              { "type": "separator", "margin": "xl" },
              {
                "type": "box", "layout": "vertical", "margin": "xl", "spacing": "sm",
                "contents": [
                  { "type": "box", "layout": "horizontal", "contents": [
                      { "type": "text", "text": "ลูกค้า", "size": "sm", "color": "#888888", "flex": 1 },
                      { "type": "text", "text": booking.customer_name || '-', "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" }
                  ]},
                  { "type": "box", "layout": "horizontal", "contents": [
                      { "type": "text", "text": "บริการ", "size": "sm", "color": "#888888", "flex": 1 },
                      { "type": "text", "text": sType, "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" }
                  ]},
                  { "type": "box", "layout": "horizontal", "contents": [
                      { "type": "text", "text": "เวลานัด", "size": "sm", "color": "#888888", "flex": 1 },
                      { "type": "text", "text": `${booking.date} เวลา ${booking.time} น.`, "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" }
                  ]},
                  { "type": "box", "layout": "horizontal", "contents": [
                      { "type": "text", "text": "อัตราค่าครู", "size": "sm", "color": "#888888", "flex": 1 },
                      { "type": "text", "text": booking.price_tier || '-', "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" }
                  ]}
                ]
              },
              { "type": "separator", "margin": "xl" },
              {
                "type": "box", "layout": "horizontal", "margin": "xl",
                "contents": [
                  { "type": "text", "text": "ยอดโอน", "size": "md", "color": "#555555", "flex": 1 },
                  { "type": "text", "text": booking.price ? `฿${booking.price.toLocaleString()}` : "รอประเมินตามเวลาเกิด", "size": "md", "color": "#D4AF37", "flex": 2, "align": "end", "weight": "bold", "wrap": true }
                ]
              },
              { "type": "separator", "margin": "xl" },
              {
                "type": "box",
                "layout": "vertical",
                "margin": "xl",
                "spacing": "sm",
                "contents": [
                  { "type": "text", "text": "ข้อมูลส่วนตัว (ดวงชะตา)", "size": "sm", "color": "#1E3A8A", "weight": "bold" },
                  { "type": "text", "text": "โทร: " + (booking.customer_details?.phone || "-"), "size": "xs", "color": "#555555", "wrap": true },
                  { "type": "text", "text": "ว/ด/ป: " + (booking.customer_details?.dob || "-"), "size": "xs", "color": "#555555", "wrap": true },
                  { "type": "text", "text": "เวลาเกิด: " + (booking.customer_details?.tob || "ไม่ระบุ"), "size": "xs", "color": "#555555", "wrap": true },
                  { "type": "text", "text": "ส่วนสูง/เพศ: " + (booking.customer_details?.height || "-") + " ซม. / " + (booking.customer_details?.gender || "-"), "size": "xs", "color": "#555555", "wrap": true },
                  { "type": "text", "text": "จังหวัด/ประเทศ: " + (booking.customer_details?.pob || "-"), "size": "xs", "color": "#555555", "wrap": true },
                  { "type": "text", "text": "คำถาม: " + (booking.customer_details?.question || "-"), "size": "xs", "color": "#555555", "wrap": true }
                ]
              }
            ]
          },
          "footer": {
            "type": "box", "layout": "vertical", "paddingAll": "lg",
            "contents": [
              {
                "type": "button",
                "action": { "type": "uri", "label": "ตรวจสอบสลิป", "uri": `${window.location.origin}/#/admin/verify` },
                "style": "primary",
                "color": "#1E3A8A",
                "height": "sm"
              }
            ]
          }
        };

        const allUsers = await getAllUsers();
        const adminIds = allUsers.filter(u => u.role === 'admin').map(u => u.id);
        if (adminIds.length > 0) {
            console.log('Sending Admin Alert (Flex) to IDs:', adminIds);
            await sendNotification(adminIds, `🔔 คิวใหม่เข้า! (คิวที่ ${queueNumber})`, null, adminFlexNew);
        }
        // Optional: Alert flow completed cleanly
        // alert(`ทดสอบ 1: ยิงจองคิวสำเร็จไปหา: ${booking.user_id}`);
    } catch (err) {
        console.error('Failed to notifyNewBooking', err);
    }
}

/**
 * Send confirmation and schedule 3 hour reminder
 */
export async function notifyBookingConfirmed(booking) {
    try {
        const settings = await getAdminSettingsDB() || {};

        // 1. Send confirmation to user
        const defaultConfirm = `✅ ยืนยันคิวแล้ว!\n\nรูปแบบ: {{service_name}}\nวันที่: {{date_time}}\nราคา: ฿{{price}}\n\n🏦 ชำระในประเทศ:\nธนาคาร: {{bank_name}}\nชื่อบัญชี: {{account_name}}\nเลขบัญชี: {{account_number}}\nพร้อมเพย์: {{promptpay}}\n\n🌍 ชำระต่างประเทศ:\nBank: {{inter_bank_name}}\nSWIFT: {{inter_swift}}\nBranch: {{inter_branch}}\nName: {{inter_account_name}}\nAccount: {{inter_account_number}}\n\n📋 เมื่อชำระเงินส่งสลิปแล้ว กรุณากรอกข้อมูลดวงชะตาของท่านที่ลิงก์นี้:\n{{form_link}}`;
        const confirmedMsg = renderTemplate(settings.template_confirmed || defaultConfirm, booking, settings);
        
        const serviceName = booking.service_type || 'บริการดูดวง';
        const dateTimeStr = `${booking.date || ''} เวลา ${booking.time || ''} น.`;
        const priceStr = booking.price ? `฿${booking.price.toLocaleString()}` : 'รอประเมินจากเวลาเกิด';

        const infoText = "📌 โอนเงินแล้ว นำสลิปมาแนบหรือส่งในแชทนี้ได้เลยค่ะ!";
        const infoColor = "#dc3545";

        let qrUrl = null;
        if (settings.qr_code_url && settings.qr_code_url.startsWith('https://')) {
            qrUrl = settings.qr_code_url;
        }

        const bodyContents = [
              { "type": "text", "text": "ASTROLOGY BOOKING", "color": "#00B900", "size": "xs", "weight": "bold" },
              { "type": "text", "text": "ยืนยันคิวแล้ว!", "weight": "bold", "size": "xxl", "color": "#111111", "margin": "md", "wrap": true },
              { "type": "text", "text": "กรุณาชำระเงินเพื่อล็อกคิวดังกล่าว", "size": "xs", "color": "#555555", "margin": "xs", "wrap": true },
              { "type": "separator", "margin": "xxl" },
              
              // DETAILS
              {
                "type": "box",
                "layout": "vertical",
                "margin": "xxl",
                "spacing": "sm",
                "contents": [
                  {
                    "type": "box",
                    "layout": "horizontal",
                    "contents": [
                      { "type": "text", "text": "รูปแบบ", "size": "sm", "color": "#888888" },
                      { "type": "text", "text": serviceName, "size": "sm", "color": "#111111", "align": "end", "wrap": true }
                    ]
                  },
                  {
                    "type": "box",
                    "layout": "horizontal",
                    "contents": [
                      { "type": "text", "text": "วันที่", "size": "sm", "color": "#888888" },
                      { "type": "text", "text": dateTimeStr, "size": "sm", "color": "#111111", "align": "end", "wrap": true }
                    ]
                  }
                ]
              },
              { "type": "separator", "margin": "xxl" },

              // PRICE
              {
                "type": "box",
                "layout": "horizontal",
                "margin": "xxl",
                "contents": [
                  { "type": "text", "text": "ยอดชำระ (TOTAL)", "size": "sm", "color": "#555555" },
                  { "type": "text", "text": priceStr, "size": "lg", "color": "#D4AF37", "align": "end", "weight": "bold", "wrap": true }
                ]
              },
              { "type": "separator", "margin": "xxl" },

              // PAYMENT INFO (INTERNATIONAL)
              {
                 "type": "box",
                 "layout": "vertical",
                 "margin": "lg",
                 "spacing": "xs",
                 "contents": [
                    { "type": "text", "text": "ชำระต่างประเทศ (International)", "weight": "bold", "color": "#111111", "size": "sm" },
                    { 
                        "type": "box", "layout": "horizontal", "margin": "sm", 
                        "contents": [
                            { "type": "text", "text": `Bank: ${settings.inter_bank_name || 'Krung Thai Bank'}`, "size": "xs", "color": "#555555" },
                            { "type": "text", "text": `${settings.inter_account_number || '521-050-9974'}`, "size": "sm", "color": "#111111", "weight": "bold", "align": "end" }
                        ]
                    },
                    { "type": "text", "text": `Swift: ${settings.inter_swift || 'KRTHTHBK'}`, "size": "xs", "color": "#555555" },
                    { "type": "text", "text": `Name: ${settings.inter_account_name || 'Ms. Sassihorn Pattaragrida'}`, "size": "xs", "color": "#555555", "wrap": true }
                 ]
              }
        ];

        if (qrUrl) {
            bodyContents.push({
               "type": "box",
               "layout": "vertical",
               "margin": "xxl",
               "contents": [
                 {
                   "type": "image",
                   "url": qrUrl,
                   "size": "xl",
                   "aspectRatio": "1:1",
                   "gravity": "center",
                   "action": {
                     "type": "uri",
                     "uri": qrUrl
                   }
                 },
                 {
                   "type": "text",
                   "text": "แตะที่รูปเพื่อดูภาพใหญ่และบันทึก หรือบันทึกจากรูปภาพด้านล่างได้เลยค่ะ",
                   "size": "xxs",
                   "color": "#666666",
                   "align": "center",
                   "margin": "md",
                   "wrap": true
                 }
               ]
            });
        }
        
        const confirmFlex = {
          "type": "bubble",
          "size": "mega",
          "body": {
            "type": "box",
            "layout": "vertical",
            "paddingAll": "xl",
            "contents": bodyContents
          },
          "footer": {
            "type": "box",
            "layout": "vertical",
            "paddingAll": "lg",
            "contents": [
              {
                "type": "text",
                "text": infoText,
                "wrap": true,
                "size": "sm",
                "color": infoColor,
                "weight": "bold",
                "align": "center",
                "margin": "sm"
              }
            ]
          }
        };

        await sendNotification(booking.user_id, "✅ แอดมินยืนยันคิวแล้ว! กรุณาชำระเงิน", qrUrl, confirmFlex);


        // 2. Schedule Reminder 3 hours before
        if (settings.template_reminder && booking.date && booking.time) {
            const reminderMsg = renderTemplate(settings.template_reminder, booking, settings);
            
            // Format time for Unix epoch (seconds) - Assuming booking time is Thai local time
            // Example: "2026-04-01T14:30:00+07:00" -> minus 3 hours
            const bookingDateTimeStr = `${booking.date}T${booking.time}:00+07:00`;
            const bookingDateMs = new Date(bookingDateTimeStr).getTime();
            if (!isNaN(bookingDateMs)) {
                // Reminder trigger happens 3 hours (10800 seconds) before the event
                const triggerTime = Math.floor(bookingDateMs / 1000) - (3 * 60 * 60);

                await fetch(`${API_BASE}/save_reminder.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: booking.id,
                        user_id: booking.user_id,
                        message: reminderMsg,
                        trigger_time: triggerTime
                    })
                });

                // --- 2.5 Admin Reminder 15 Mins Before ---
                const adminTriggerTime = Math.floor(bookingDateMs / 1000) - (15 * 60); // 15 mins before
                const SERVICE_MAP_LOCAL = {
                    onsite: t('service_onsite').replace('🏠 ', ''),
                    chat: t('service_chat').replace('💬 ', ''),
                    call: t('service_call').replace('📞 ', ''),
                    video: t('service_video').replace('📹 ', '')
                };
                const sType = SERVICE_MAP_LOCAL[booking.service_type] || booking.service_type || '-';
                
                const adminFlexReminder = {
                  "type": "bubble",
                  "size": "mega",
                  "header": {
                    "type": "box", "layout": "vertical", "paddingAll": "20px", "backgroundColor": "#D4AF37",
                    "contents": [ { "type": "text", "text": "⏰ ใกล้ถึงคิวแล้ว! (Reminder)", "color": "#111111", "weight": "bold", "size": "lg" } ]
                  },
                  "body": {
                    "type": "box", "layout": "vertical", "paddingAll": "xl",
                    "contents": [
                      { "type": "text", "text": booking.customer_name || 'ลูกค้า', "weight": "bold", "size": "xl", "color": "#111111" },
                      { "type": "text", "text": "อีก 15 นาทีจะถึงเวลานัดหมาย", "size": "sm", "color": "#555555" },
                      { "type": "separator", "margin": "xl" },
                      {
                        "type": "box", "layout": "vertical", "margin": "xl", "spacing": "sm",
                        "contents": [
                          { "type": "box", "layout": "horizontal", "contents": [ { "type": "text", "text": "บริการ", "size": "sm", "color": "#888888", "flex": 1 }, { "type": "text", "text": sType, "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" } ]},
                          { "type": "box", "layout": "horizontal", "contents": [ { "type": "text", "text": "เวลานัด", "size": "sm", "color": "#888888", "flex": 1 }, { "type": "text", "text": `${booking.time} น.`, "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" } ]}
                        ]
                      }
                    ]
                  },
                  "footer": {
                    "type": "box", "layout": "vertical", "paddingAll": "lg",
                    "contents": [
                      {
                        "type": "button",
                        "action": { "type": "uri", "label": "เปิดดูข้อมูลลูกค้า", "uri": `${window.location.origin}/#/booking/${booking.id}/payment` },
                        "style": "primary", "color": "#111111", "height": "sm"
                      }
                    ]
                  }
                };

                const adminMsgJsonStr = JSON.stringify(adminFlexReminder);
                
                const allUsers = await getAllUsers();
                const adminIds = allUsers.filter(u => u.role === 'admin').map(u => u.id);
                for(let admId of adminIds) {
                    await fetch(`${API_BASE}/save_reminder.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id: `adm_${booking.id}_${admId}`, // unique id
                            user_id: admId,
                            message: adminMsgJsonStr,
                            trigger_time: adminTriggerTime
                        })
                    });
                }
            }
        }
        
        // Push event to Google Calendar automatically when Admin confirms the booking
        await notifyGoogleCalendar(booking);

    } catch (err) {
        console.error('Failed to notifyBookingConfirmed', err);
    }
}

/**
 * Send confirmation of astrology data submission
 */
export async function notifyAstrologyDataSubmitted(booking, details) {
    try {
        const bodyContents = [
              { "type": "text", "text": "ASTROLOGY DATA", "color": "#06C755", "size": "xs", "weight": "bold" },
              { "type": "text", "text": "ยืนยันการส่งข้อมูล", "weight": "bold", "size": "xxl", "color": "#111111", "margin": "md", "wrap": true },
              { "type": "text", "text": "ข้อมูลดวงชะตาของคุณถูกส่งให้หมอดูเพื่อจัดเตรียมความพร้อมเรียบร้อยแล้วค่ะ", "size": "xs", "color": "#555555", "margin": "xs", "wrap": true },
              { "type": "separator", "margin": "xxl" },
              {
                "type": "box",
                "layout": "vertical",
                "margin": "xxl",
                "spacing": "sm",
                "contents": [
                   { "type": "box", "layout": "horizontal", "contents": [{ "type": "text", "text": "เพศ / ส่วนสูง", "size": "sm", "color": "#555555"}, { "type": "text", "text": `${details.gender || '-'} / ${details.height || '-'}`, "size": "sm", "color": "#111111", "align": "end", "wrap": true}] },
                   { "type": "box", "layout": "horizontal", "contents": [{ "type": "text", "text": "ว/ด/ป เกิด", "size": "sm", "color": "#555555"}, { "type": "text", "text": details.dob || '-', "size": "sm", "color": "#111111", "align": "end", "wrap": true}] },
                   { "type": "box", "layout": "horizontal", "contents": [{ "type": "text", "text": "เวลาเกิด", "size": "sm", "color": "#555555"}, { "type": "text", "text": details.tob || '-', "size": "sm", "color": "#111111", "align": "end", "wrap": true}] },
                   { "type": "box", "layout": "horizontal", "contents": [{ "type": "text", "text": "สถานที่เกิด", "size": "sm", "color": "#555555"}, { "type": "text", "text": details.pob || '-', "size": "sm", "color": "#111111", "align": "end", "wrap": true}] },
                   { "type": "box", "layout": "horizontal", "contents": [{ "type": "text", "text": "คำถามหลัก", "size": "sm", "color": "#555555"} ] },
                   { "type": "text", "text": details.question || '-', "size": "sm", "color": "#111111", "wrap": true, "margin": "sm" }
                ]
              }
        ];
        
        const formLink = `${window.location.origin}/#/booking/${booking.id}/payment`;
        const flexMsg = {
          "type": "bubble",
          "size": "mega",
          "body": {
            "type": "box",
            "layout": "vertical",
            "paddingAll": "xl",
            "contents": bodyContents
          },
          "footer": {
            "type": "box", "layout": "vertical", "paddingAll": "lg",
            "contents": [{
               "type": "button", "action": { "type": "uri", "label": "📝 เข้าไปแก้ไขข้อมูลอีกครั้ง", "uri": formLink },
               "style": "secondary", "color": "#f0f0f0", "height": "sm", "margin": "none"
            }]
          }
        };

        await sendNotification(booking.user_id, "ส่งข้อมูลดวงชะตาเรียบร้อย ✨", null, flexMsg);

        // Notify Admins
        const allUsers = await getAllUsers();
        const adminIds = allUsers.filter(u => u.role === 'admin').map(u => u.id);
        if (adminIds.length > 0) {
            const serviceMap = {
                'onsite': 'นัดพบ',
                'chat': 'แชท',
                'call': 'โทร',
                'video': 'วีดีโอคอล'
            };
            const sType = serviceMap[booking.service_type] || booking.service_type;
            const adminFlexData = {
              "type": "bubble",
              "size": "mega",
              "header": {
                "type": "box", "layout": "vertical", "paddingAll": "20px", "backgroundColor": "#4338CA",
                "contents": [ { "type": "text", "text": "📋 ข้อมูลดวงชะตาใหม่!", "color": "#FFFFFF", "weight": "bold", "size": "lg" } ]
              },
              "body": {
                "type": "box", "layout": "vertical", "paddingAll": "xl",
                "contents": [
                  { "type": "text", "text": booking.customer_name || 'ลูกค้า', "weight": "bold", "size": "xl", "color": "#4338CA" },
                  { "type": "text", "text": `บริการ: ${sType}`, "size": "sm", "color": "#555555" },
                  { "type": "separator", "margin": "xl" },
                  {
                    "type": "box", "layout": "vertical", "margin": "xl", "spacing": "sm",
                    "contents": [
                      { "type": "box", "layout": "horizontal", "contents": [ { "type": "text", "text": "วันเกิด", "size": "sm", "color": "#888888", "flex": 1 }, { "type": "text", "text": `${details.dob} ${details.tob}`, "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" } ]},
                      { "type": "box", "layout": "horizontal", "contents": [ { "type": "text", "text": "ข้อมูล", "size": "sm", "color": "#888888", "flex": 1 }, { "type": "text", "text": `${details.gender||'-'} / ${details.height||'-'}ซม.`, "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" } ]},
                      { "type": "box", "layout": "horizontal", "contents": [ { "type": "text", "text": "สถานที่เกิด", "size": "sm", "color": "#888888", "flex": 1 }, { "type": "text", "text": details.pob||'-', "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" } ]}
                    ]
                  },
                  { "type": "separator", "margin": "xl" },
                  { "type": "text", "text": "คำถาม:", "size": "sm", "color": "#888888", "margin": "xl" },
                  { "type": "text", "text": details.question||'-', "size": "sm", "color": "#111111", "wrap": true, "weight": "bold" }
                ]
              },
              "footer": {
                "type": "box", "layout": "vertical", "paddingAll": "lg",
                "contents": [
                  {
                    "type": "button",
                    "action": { "type": "uri", "label": "เปิดดูรายละเอียดทั้งหมด", "uri": `${window.location.origin}/#/booking/${booking.id}/payment` },
                    "style": "secondary", "height": "sm"
                  }
                ]
              }
            };
            await sendNotification(adminIds, "📝 ข้อมูลดวงชะตาเข้าแล้ว", null, adminFlexData);
        }

        booking.customer_details = details; 
        // Sync to GCal removed per user request to only sync once during admin confirmation.
    } catch (err) {
        console.error('Failed to notify data submitted', err);
    }
}

/**
 * Send alert to admins when booking is cancelled
 */
export async function notifyBookingCancelled(booking) {
    try {
        const allUsers = await getAllUsers();
        const adminIds = allUsers.filter(u => u.role === 'admin').map(u => u.id);
        if (adminIds.length > 0) {
            const adminFlexCancel = {
              "type": "bubble",
              "size": "mega",
              "header": {
                "type": "box", "layout": "vertical", "paddingAll": "20px", "backgroundColor": "#DC2626",
                "contents": [ { "type": "text", "text": "❌ ลูกค้ายกเลิกคิว", "color": "#FFFFFF", "weight": "bold", "size": "lg" } ]
              },
              "body": {
                "type": "box", "layout": "vertical", "paddingAll": "xl",
                "contents": [
                  { "type": "text", "text": booking.customer_name || 'ลูกค้า', "weight": "bold", "size": "xl", "color": "#DC2626" },
                  { "type": "separator", "margin": "xl" },
                  {
                    "type": "box", "layout": "vertical", "margin": "xl", "spacing": "sm",
                    "contents": [
                      { "type": "box", "layout": "horizontal", "contents": [ { "type": "text", "text": "บริการ", "size": "sm", "color": "#888888", "flex": 1 }, { "type": "text", "text": booking.service_type, "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" } ]},
                      { "type": "box", "layout": "horizontal", "contents": [ { "type": "text", "text": "เวลานัด", "size": "sm", "color": "#888888", "flex": 1 }, { "type": "text", "text": `${booking.date} เวลา ${booking.time} น.`, "size": "sm", "color": "#111111", "flex": 2, "wrap": true, "weight": "bold" } ]}
                    ]
                  }
                ]
              }
            };
            await sendNotification(adminIds, "❌ ลูกค้ายกเลิกคิว", null, adminFlexCancel);
        }
    } catch (err) {
        console.error('Failed to notify booking cancelled', err);
    }
}

/**
 * Send thank you message when session is officially marked as completed by Admin.
 */
export async function notifyBookingCompleted(booking) {
    try {
        const settings = await getAdminSettingsDB() || {};
        const enableReview = settings.enable_review !== false; // defaults to true

        // Optional survey link or history link
        const formLink = `${window.location.origin}/#/history`;
        const flexMsg = {
          "type": "bubble",
          "size": "mega",
          "body": {
            "type": "box",
            "layout": "vertical",
            "paddingAll": "xl",
            "contents": [
              { "type": "text", "text": "ASTROLOGY SESSION", "color": "#D4AF37", "size": "xs", "weight": "bold" },
              { "type": "text", "text": "ดูดวงเสร็จสิ้น 🙏", "weight": "bold", "size": "xxl", "color": "#111111", "margin": "md", "wrap": true },
              { "type": "separator", "margin": "xxl" },
              {
                "type": "text",
                "text": "ขอบคุณที่ใช้บริการตรวจดวงชะตานะคะ\nหวังว่าคำทำนายจะเป็นแนวทางและเป็นประโยชน์ให้ท่านนำไปปรับใช้ในชีวิตประจำวันค่ะ ✨",
                "wrap": true,
                "size": "sm",
                "color": "#333333",
                "margin": "xl",
                "align": "start"
              }
            ]
          },
          "footer": {
            "type": "box",
            "layout": "vertical",
            "paddingAll": "lg",
            "contents": [
              {
                "type": "button",
                "action": {
                  "type": "uri",
                  "label": "📋 ดูประวัติการจอง",
                  "uri": formLink
                },
                "style": "secondary",
                "color": "#f0f0f0",
                "height": "sm",
                "margin": "none"
              }
            ]
          }
        };

        if (!enableReview) {
            delete flexMsg.footer;
        }

        await sendNotification(booking.user_id, "ดูดวงเสร็จสิ้น 🙏", null, flexMsg);
    } catch (err) {
        console.error('Failed to notify booking completed', err);
    }
}

/**
 * Send Booking Data to Google Calendar Setup (via GAS Webhook or Make.com)
 * Called automatically when Admin confirms the booking.
 */
export async function notifyGoogleCalendar(booking) {
    try {
        const settings = await getAdminSettingsDB() || {};
        // Use user-provided webhook, fallback to settings if somehow overridden
        const webhookUrl = settings.google_calendar_webhook || 'https://script.google.com/macros/s/AKfycbw8RbCi8OwgLwWhNXDWw46B5BpsSprPAODIugjDJluRhmmncBJFbUSwoEdXhbAHq_8F/exec';
        
        if (!webhookUrl || !webhookUrl.startsWith('https://')) {
            console.log('Skipping Google Calendar: Webhook URL not set.');
            return;
        }

        const SERVICE_MAP = {
            onsite: t('service_onsite').replace('🏠 ', ''),
            chat: t('service_chat').replace('💬 ', ''),
            call: t('service_call').replace('📞 ', ''),
            video: t('service_video').replace('📹 ', '')
        };
        const serviceName = SERVICE_MAP[booking.service_type] || booking.service_type || 'ดูดวง';
        const details = booking.customer_details || {};

        const payload = {
            webhookUrl: webhookUrl, // Handled by PHP proxy
            id: String(booking.id || ''),
            name: String(booking.customer_name || 'ลูกค้า'),
            service: String(serviceName),
            date: String(booking.date || ''),
            time: String(booking.time || ''),
            question: String(details.question || '-'),
            dob: String(details.dob || '-'),
            tob: String(details.tob || '-'),
            gender: String(details.gender || '-'),
            pob: String(details.pob || '-')
        };

        // Use PHP proxy to push data payload to Apps Script. 
        // This overcomes browser CORS and reliably handles redirects!
        const res = await fetch('/api/gcal.php', {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: { 'Content-Type': 'application/json' }
        });
        
        const proxyResp = await res.json();
        console.log('Google Calendar Proxy resp:', proxyResp);
        
        console.log('Google Calendar Webhook triggered successfully via proxy.');
    } catch (err) {
        console.error('Failed to push to Google Calendar Webhook proxy:', err);
    }
}
